import { useMutation } from '@tanstack/react-query'
import posthog from 'posthog-js'
import invariant from 'tiny-invariant'

import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import useNotyf from '@/hooks/useNotyf'
import {
  CLIENT_CHANGED,
  COMPANY_CHANGED,
  GROUP_CHANGED,
  NETWORK_CHANGED,
  useUserContextChanged,
} from '@/hooks/useUserContextChanged'
import { COMPANY_TYPE_CODE, POSTHOG_EVENT } from '@/lib/constants'
import { isOfficeParent } from '@/lib/helpers/officeHelpers'
import { skipNavigationPromptOnce } from '@/lib/navigationPrompt'
import { ROUTES } from '@/lib/routes'
import authService from '@/lib/services/authService'
import userService from '@/lib/services/userService'
import {
  selectCompanies,
  selectCurrentCompany,
  selectCurrentGroup,
  selectCurrentUser,
  selectGroups,
  selectIsAuthenticated,
  selectIsAuthenticatedClient,
  selectIsGroupUser,
  selectIsInitialized,
  selectIsUpdatingContext,
  selectUserTypes,
  setIsUpdatingContext,
  signOutSuccess,
} from '@/redux/slices/session'
import { USER_TYPE } from '@/types/misc'

interface UpdateGroupContextMutationVariables {
  group: IRI
  redirectUri?: string
}

interface UpdateNetworkContextMutationVariables {
  network: IRI
}

interface UpdateCompanyContextMutationVariables {
  company: IRI
  redirectUri?: string
}

// TODO: Rename to useSession
export function useAuth() {
  const { postContextChange } = useUserContextChanged()
  const userTypes = useAppSelector(selectUserTypes)
  const currentGroup = useAppSelector(selectCurrentGroup)
  const currentCompany = useAppSelector(selectCurrentCompany)
  const companies = useAppSelector(selectCompanies)
  const groups = useAppSelector(selectGroups)
  const isAuthenticatedClient = useAppSelector(selectIsAuthenticatedClient)
  const isInitialized = useAppSelector(selectIsInitialized)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const currentUser = useAppSelector(selectCurrentUser)
  const isUpdatingContext = useAppSelector(selectIsUpdatingContext)
  const isGroupUser = useAppSelector(selectIsGroupUser)
  const notyf = useNotyf()
  const dispatch = useAppDispatch()

  const updateGroupContextMutation = useMutation({
    mutationFn: (variables: UpdateGroupContextMutationVariables) => {
      invariant(currentUser, 'Expected currentUser to be defined')

      dispatch(setIsUpdatingContext(true))

      return userService.updateUserContext(currentUser.id, {
        group: variables.group,
        company: null,
        scope: USER_TYPE.group,
      })
    },
    onSuccess: (response, variables) => {
      postContextChange(GROUP_CHANGED, {
        group: { id: response.group.id },
      })

      // TODO: Use a different localStorage key to store the previously selected
      // group ID or rename it to something more generic
      authService.setUserSelectedCompanyId(response.group.id)
      authService.setUserSelectedScope(USER_TYPE.group)
      posthog.capture(POSTHOG_EVENT.switchGroup, {
        $set: { lastGroup: response.group.name },
      })

      skipNavigationPromptOnce()
      if (isOfficeParent()) {
        window.location.reload()
      } else {
        window.location.assign(variables.redirectUri ?? ROUTES.index())
      }
    },
    onError: (error) => {
      notyf.error('Could not switch group')
      console.log(error)
    },
    onSettled: () => {
      dispatch(setIsUpdatingContext(false))
    },
  })

  const updateNetworkContextMutation = useMutation({
    mutationFn: (variables: UpdateNetworkContextMutationVariables) => {
      invariant(currentUser, 'Expected currentUser to be defined')

      dispatch(setIsUpdatingContext(true))

      return userService.updateUserContext(currentUser.id, {
        group: null,
        company: null,
        network: variables.network,
        scope: USER_TYPE.network,
      })
    },
    onSuccess: (response) => {
      postContextChange(NETWORK_CHANGED, {
        network: { id: response.network.id },
      })

      // The API reads the network ID from the same "previously selected"
      // localStorage key as companies and groups when picking the scope on load
      authService.setUserSelectedCompanyId(response.network.id)
      authService.setUserSelectedScope(USER_TYPE.network)
      posthog.capture(POSTHOG_EVENT.switchNetwork, {
        $set: { lastNetwork: response.network.name },
      })

      skipNavigationPromptOnce()
      window.location.assign(ROUTES.networkOverview())
    },
    onError: () => {
      notyf.error('Could not switch network')
    },
    onSettled: () => {
      dispatch(setIsUpdatingContext(false))
    },
  })

  const updateCompanyContextMutation = useMutation({
    mutationFn: (variables: UpdateCompanyContextMutationVariables) => {
      invariant(currentUser, 'Expected currentUser to be defined')

      dispatch(setIsUpdatingContext(true))

      return userService.updateUserContext(currentUser.id, {
        group: null,
        company: variables.company,
        scope: USER_TYPE.company,
      })
    },
    onSuccess: (response, variables) => {
      postContextChange(COMPANY_CHANGED, {
        company: { id: response.company.id },
      })
      authService.setUserSelectedCompanyId(response.company.id)
      authService.setUserSelectedScope(USER_TYPE.company)
      posthog.capture(POSTHOG_EVENT.switchCompany, {
        $set: { lastCompany: response.company.name },
      })

      skipNavigationPromptOnce()
      window.location.assign(variables.redirectUri ?? ROUTES.index())
    },
    onError: () => {
      notyf.error('Could not switch company')
    },
    onSettled: () => {
      dispatch(setIsUpdatingContext(false))
    },
  })

  const updateClientContextMutation = useMutation({
    mutationFn: () => {
      invariant(currentUser, 'Expected currentUser to be defined')
      dispatch(setIsUpdatingContext(true))

      return userService.updateUserContext(currentUser.id, {
        group: null,
        company: null,
        scope: USER_TYPE.client,
      })
    },
    onSuccess: () => {
      postContextChange(CLIENT_CHANGED, {})
      authService.setUserSelectedScope(USER_TYPE.client)
      posthog.capture(POSTHOG_EVENT.switchClient, { $set: {} })
      skipNavigationPromptOnce()
      window.location.assign(ROUTES.index())
    },
    onError: () => {
      notyf.error('Could not switch to client account')
    },
    onSettled: () => {
      dispatch(setIsUpdatingContext(false))
    },
  })

  function register(redirectUri?: string) {
    authService.registerAsCompanyUser(redirectUri)
  }

  function signOut(redirectUri?: string) {
    dispatch(signOutSuccess())
    authService.logout(redirectUri)
    posthog.reset()
    posthog.capture(POSTHOG_EVENT.logout)
  }

  function updateGroupContext(options: UpdateGroupContextMutationVariables) {
    updateGroupContextMutation.mutate(options)
  }

  function updateNetworkContext(
    options: UpdateNetworkContextMutationVariables,
  ) {
    updateNetworkContextMutation.mutate(options)
  }

  function updateCompanyContext(
    options: UpdateCompanyContextMutationVariables,
  ) {
    updateCompanyContextMutation.mutate(options)
  }

  function updateClientContext() {
    updateClientContextMutation.mutate()
  }

  const companyCode = currentCompany?.companyType.code
  const isIcaewUser = currentUser?.permissions?.includes('icaew/view') || false

  const isCurrentCompanyDomainLoginGranted =
    isGroupUser ||
    isIcaewUser ||
    isAuthenticatedClient ||
    !currentCompany ||
    (currentCompany &&
      currentUser &&
      currentUser.companyIdsGrantedWithLoginMethod.includes(currentCompany.id))

  return {
    currentUser,
    companies,
    groups,
    userTypes,
    isInitialized,
    isAuthenticated,
    isGuest: !isAuthenticated,
    currentGroup,
    currentCompany,
    isCurrentCompanyDomainLoginGranted,
    isCompanyUser: !!currentCompany,
    register,
    signOut,
    updateGroupContext,
    updateNetworkContext,
    updateCompanyContext,
    isUpdatingContext,
    updateClientContext,
    isFcaFirm: currentCompany?.companyType.fca || false,
    isIfa: companyCode === COMPANY_TYPE_CODE.ifa,
    isAccountant: companyCode === COMPANY_TYPE_CODE.accountant,
    isSolicitor: companyCode === COMPANY_TYPE_CODE.solicitor,
    isRegulatedByIcaew: currentCompany?.isRegulatedByIcaew || false,
    isGroupUser,
    isIcaewUser,
    isClientUser: isAuthenticatedClient,
  }
}
